import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { SdTestAppBenchClient } from "@/lib/sdtestapp/sdTestAppBenchClient";

export function BenchBatchCustomSequencesModal({
  open,
  port,
  onClose,
  onChanged,
}: {
  open: boolean;
  port: string;
  onClose: () => void;
  onChanged: () => void;
}): JSX.Element | null {
  const [sequenceName, setSequenceName] = useState("");
  const [manualSequence, setManualSequence] = useState("");
  const [description, setDescription] = useState("");
  const [deleteName, setDeleteName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!open) return null;

  const client = new SdTestAppBenchClient(port);

  const run = async (action: () => Promise<void>) => {
    setPending(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : String(actionError));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded border bg-background p-4 shadow-lg">
        <h3 className="text-lg font-semibold">Manage custom sequences</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Create sequences from a manual sequence string, or delete an existing custom sequence by name.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <Label htmlFor="custom-sequence-name">Sequence name</Label>
            <Input
              id="custom-sequence-name"
              className="mt-1"
              value={sequenceName}
              onChange={(event) => setSequenceName(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="custom-sequence-description">Description</Label>
            <Input
              id="custom-sequence-description"
              className="mt-1"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="custom-manual-sequence">Manual sequence</Label>
            <textarea
              id="custom-manual-sequence"
              className="mt-1 block w-full rounded border px-2 py-1 font-mono text-sm"
              rows={4}
              value={manualSequence}
              onChange={(event) => setManualSequence(event.target.value)}
            />
          </div>
          <Button
            type="button"
            disabled={pending || sequenceName.trim() === "" || manualSequence.trim() === ""}
            onClick={() => void run(async () => {
              await client.createCustomSequence({
                sequenceName: sequenceName.trim(),
                description: description.trim(),
                manualSequence: manualSequence.trim(),
              });
              setSequenceName("");
              setManualSequence("");
              setDescription("");
            })}
          >
            Save custom sequence
          </Button>
        </div>

        <div className="mt-6 space-y-3 border-t pt-4">
          <div>
            <Label htmlFor="custom-delete-name">Delete sequence name</Label>
            <Input
              id="custom-delete-name"
              className="mt-1"
              value={deleteName}
              onChange={(event) => setDeleteName(event.target.value)}
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            disabled={pending || deleteName.trim() === ""}
            onClick={() => void run(async () => {
              await client.deleteCustomSequence(deleteName.trim());
              setDeleteName("");
            })}
          >
            Delete custom sequence
          </Button>
        </div>

        {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}

        <div className="mt-4 flex justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
